import { type ApolloError, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import type { Certificates_ProductType as CertificateProductType } from '__generated__/graphql-types';

import VerifiedSkillCourseCertificateByProductQuery from 'bundles/skills-common/shared/verified-skills/data/certificate/VerifiedSkillCourseCertificateByProductQuery.graphql';
import type {
  GetVerifiedSkillCourseCertificateByProductQuery,
  GetVerifiedSkillCourseCertificateByProductQueryVariables,
} from 'bundles/skills-common/shared/verified-skills/data/certificate/__generated__/VerifiedSkillCourseCertificateByProductQuery';

type UseGetVerifiedSkillCourseCertificateParams = {
  productItemId: string;
  productType: CertificateProductType;
  shouldSkipQuery: boolean;
};

/**
 * Course / certificate product query for verified-skills flows (e.g. result template completion checks).
 * Duplicates certificate-common hook logic so skills-common does not depend on that bundle.
 */
export const useGetVerifiedSkillCourseCertificate = ({
  productItemId,
  productType,
  shouldSkipQuery,
}: UseGetVerifiedSkillCourseCertificateParams) => {
  const { data, loading, error } = useQuery<
    GetVerifiedSkillCourseCertificateByProductQuery,
    GetVerifiedSkillCourseCertificateByProductQueryVariables
  >(VerifiedSkillCourseCertificateByProductQuery, {
    variables: {
      productItemId,
      productType,
    },
    skip: shouldSkipQuery || !productItemId,
    onError: (err: ApolloError) => {
      Sentry.captureException(err, {
        tags: {
          hook: 'useGetVerifiedSkillCourseCertificate',
          productItemId,
          productType,
        },
      });
    },
  });

  const certificateData = data?.Certificate?.getMyCertificateByProduct;

  return {
    certificateData,
    loading,
    error,
  };
};

export default useGetVerifiedSkillCourseCertificate;
